import React from "react";
import { SurveyDetailsModel } from "../../../surveys/models/SurveyDetailsModel";
import { Instance } from "mobx-state-tree";
import {
  CornerRadius,
  FoundationColorTokens,
  Spacing,
  Typography as LeoTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { BorderStyle, PRIMARY_COLORS } from "@pulse/shared-components";
import { TFunction } from "i18next";
import { NavigateToFunctions } from "../../../../routes/RoutesHelper";
import { Grid, Stack, Typography } from "@mui/material";
import { DividerComponent } from "../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";
import {
  Button,
  Chip,
  EllipsisTypography,
  IconButton,
} from "@surya-digital/leo-reactjs-material-ui";
import { getSurveyStatusChipProps } from "../../../utils/getSurveyStatusChipProps";
import { Copy } from "lucide-react";
import { LinkInfo, LinkInfoProps } from "./LinkInfo";
import { SurveyListElementChart } from "./SurveyListElementChart";
import { SurveyDataProgressBar } from "./SurveyDataProgressBar";

const SURVEY_DETAIL_LIST_ELEMENT_WIDTH = "1344px";
const SURVEY_DETAIL_SECTION_WIDTH = "540px";
const SURVEY_LIST_ELEMENT_CHART_HEIGHT = "308px";

export interface SurveyDetailListElementProps {
  surveyDetails: Instance<typeof SurveyDetailsModel>;
  spacing: Spacing;
  border: BorderStyle;
  borderRadius: CornerRadius;
  tokens: FoundationColorTokens<string>;
  typography: LeoTypography;
  t: TFunction;
  navigateTo: NavigateToFunctions;
  projectId: string;
  setSelectedSurveyId: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedSurveyName: React.Dispatch<React.SetStateAction<string | null>>;
  setIsCloneSurveyDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SurveyDetailListElement = ({
  surveyDetails,
  spacing,
  border,
  borderRadius,
  tokens,
  typography,
  t,
  navigateTo,
  projectId,
  setSelectedSurveyId,
  setSelectedSurveyName,
  setIsCloneSurveyDialogOpen,
}: SurveyDetailListElementProps): React.ReactElement => {
  const linkInfos: Pick<LinkInfoProps, "title" | "linkNumber">[] = [
    {
      title: t("projects.surveyDetails.totalLinksText"),
      linkNumber: surveyDetails.totalLinks,
    },
    {
      title: t("surveyLinkStatus.submitted"),
      linkNumber: surveyDetails.submittedLinks,
    },
    {
      title: t("surveyLinkStatus.inProgress"),
      linkNumber: surveyDetails.inProgressLinks,
    },
    {
      title: t("surveyLinkStatus.notStarted"),
      linkNumber: surveyDetails.notStartedLinks,
    },
  ];

  return (
    <Stack
      direction="row"
      borderRadius={borderRadius.radiusXS}
      border={border.default}
      width={SURVEY_DETAIL_LIST_ELEMENT_WIDTH}
      divider={<DividerComponent height="100%" orientation="vertical" />}
    >
      <Stack
        width={SURVEY_DETAIL_SECTION_WIDTH}
        divider={<DividerComponent width="100%" orientation="horizontal" />}
      >
        <Stack
          gap={spacing.spaceXXS}
          padding={`${spacing.spaceMD} ${spacing.spaceXL}`}
        >
          <EllipsisTypography
            width="624px"
            textColor="label"
            maxLines={1}
            typography={{ ...typography.sh2 }}
          >
            {surveyDetails.surveyName}
          </EllipsisTypography>
          <Stack direction="row" gap={spacing.spaceXXS}>
            <Typography
              {...typography.b2}
              color={tokens.labelSubtle}
              width="56px"
            >
              {t("projects.surveyDetails.creatorText")}
            </Typography>
            <EllipsisTypography
              textColor="labelSubtle"
              maxLines={1}
              typography={{ ...typography.b2 }}
              width="564px"
            >
              {t("projects.surveyDetails.creatorName", {
                creatorName: surveyDetails.creatorName,
              })}
            </EllipsisTypography>
          </Stack>
        </Stack>
        <Stack width="100%">
          <Grid
            justifyContent="center"
            bgcolor={tokens.backgroundSubtle}
            paddingBlock={spacing.spaceSM}
            paddingInline={spacing.spaceXL}
            spacing={spacing.space3XL}
            columns={surveyDetails.totalLinks ? 4 : 2}
            container
          >
            {linkInfos.map((props, index) => (
              <Grid size={1} key={index}>
                <LinkInfo
                  key={index}
                  tokens={tokens}
                  typography={typography}
                  spacing={spacing}
                  {...props}
                />
              </Grid>
            ))}
          </Grid>
          {surveyDetails.totalLinks ? (
            <Stack>
              <SurveyDataProgressBar
                surveyName={surveyDetails.surveyName}
                submitted={surveyDetails.submittedLinks}
                inProgress={surveyDetails.inProgressLinks}
                notStarted={surveyDetails.notStartedLinks}
              />
            </Stack>
          ) : null}
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          padding={`${spacing.spaceMD} ${spacing.spaceXL}`}
        >
          <IconButton
            name="clone"
            variant="outlined-neutral"
            size="medium"
            icon={<Copy />}
            onClick={() => {
              setSelectedSurveyId(surveyDetails.surveyId);
              setSelectedSurveyName(surveyDetails.surveyName);
              setIsCloneSurveyDialogOpen(true);
            }}
          />
          <Button
            onClick={() => {
              navigateTo.surveyDetails(projectId, surveyDetails.surveyId);
            }}
            name="filter"
            label={t("projects.surveyDetails.viewSurveyTitle")}
            variant="outlined-neutral"
            size="medium"
          />
        </Stack>
      </Stack>
      <Stack width="100%">
        <Stack
          direction="row"
          justifyContent="space-between"
          padding={`${spacing.spaceLG} ${spacing.space3XL}`}
        >
          <Chip {...getSurveyStatusChipProps(surveyDetails.surveyStatus, t)} />
          {!surveyDetails.isNoSubmissionDataPresent && (
            <Stack direction="row" gap={spacing.spaceXXS} alignItems="center">
              <Stack
                height="8px"
                width="8px"
                borderRadius={borderRadius.radiusMax}
                bgcolor={PRIMARY_COLORS[400]}
              />
              <Typography {...typography.b2} color={tokens.labelSubtle}>
                {t("surveyLinkStatus.submitted")}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack
          maxHeight={SURVEY_LIST_ELEMENT_CHART_HEIGHT}
          padding={`${spacing.spaceLG} ${spacing.space3XL}`}
          alignItems="center"
          gap={spacing.spaceXS}
          marginBottom={spacing.space3XL}
        >
          <SurveyListElementChart
            spacing={spacing}
            tokens={tokens}
            typography={typography}
            t={t}
            surveyDetails={surveyDetails}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
